import React from "react";
import {
  CalendarCheck, PhoneCall, Languages, UserRound, CalendarClock,
  CheckCircle2, MessageCircle, Settings2, TrendingUp, Cloud, HeartPulse,
} from "lucide-react";
import { Reveal } from "../Reveal";
import {
  appointmentSteps, concurrencyTiers, internationalLanguages,
  regionalLanguages, languageSamples,
} from "../../mock";

// Shared block shell for industries page sections
export const Block = ({ id, index, icon: Icon, title, tagline, desc, bullets, children, flip = false }) => (
  <section id={id} className="scroll-mt-24 py-16 lg:py-20">
    <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16`}>
      <Reveal className={flip ? "lg:order-2" : ""}>
        <div>
          <div className="flex items-center gap-4">
            <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-3.5">
              <Icon className="h-6 w-6 text-cyan-300" />
            </div>
            <span className="font-display text-sm font-bold text-cyan-400/60">{String(index).padStart(2, "0")}</span>
          </div>
          <h3 className="font-display mt-5 text-2xl font-bold text-white sm:text-3xl">{title}</h3>
          <p className="mt-2 text-sm font-semibold text-cyan-300">{tagline}</p>
          <p className="mt-4 leading-relaxed text-slate-400">{desc}</p>
          <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {b}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
      <Reveal delay={150} className={flip ? "lg:order-1" : ""}>
        <div className="card-dark rounded-3xl p-6 sm:p-8">{children}</div>
      </Reveal>
    </div>
  </section>
);

// ---- 1. Appointment Management visual: 5-step flow
export const AppointmentBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={CalendarCheck} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets}>
    <div className="space-y-3">
      {appointmentSteps.map((s, i) => (
        <div key={s.step} className="flex items-center gap-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/10 font-display text-sm font-bold text-cyan-300">
            {s.step}
          </div>
          <div className="flex-1 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3">
            <div className="text-sm font-semibold text-white">{s.title}</div>
            <div className="mt-0.5 text-xs text-slate-500">{s.desc}</div>
          </div>
          {i < appointmentSteps.length - 1 && <div className="h-4 w-px bg-cyan-400/20" />}
        </div>
      ))}
      <div className="flex items-center gap-2 rounded-xl border border-emerald-400/20 bg-emerald-400/5 px-4 py-3">
        <MessageCircle className="h-4 w-4 text-emerald-300" />
        <span className="text-xs text-emerald-200">"Your appointment is confirmed with Dr. Ahmed Khan — 10 Apr, 09:00 AM" ✓✓</span>
      </div>
    </div>
  </Block>
);

// ---- 2. Call concurrency visual: gauge + tiers
export const ConcurrencyBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={PhoneCall} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets} flip>
    <div>
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5">
          <PhoneCall className="h-5 w-5 text-cyan-300" />
          <div className="font-display mt-3 text-4xl font-bold text-white">15</div>
          <div className="mt-1 text-xs text-slate-400">Concurrent calls (default)</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <Settings2 className="h-5 w-5 text-slate-300" />
          <div className="font-display mt-3 text-4xl font-bold text-white">100+</div>
          <div className="mt-1 text-xs text-slate-400">Adjustable limit, on demand</div>
        </div>
      </div>
      <div className="mt-5 overflow-hidden rounded-2xl border border-white/10">
        <div className="border-b border-white/10 bg-white/[0.04] px-5 py-3 text-xs font-semibold uppercase tracking-widest text-slate-400">
          Recommended concurrency
        </div>
        {concurrencyTiers.map((t, i) => (
          <div key={t.size} className={`flex items-center justify-between px-5 py-3.5 ${i !== concurrencyTiers.length - 1 ? "border-b border-white/5" : ""}`}>
            <span className="text-sm text-slate-300">{t.size}</span>
            <span className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1 font-display text-sm font-bold text-cyan-300">{t.recommended}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-cyan-400" /> Flexible scaling</span>
        <span className="flex items-center gap-1.5"><Cloud className="h-3.5 w-3.5 text-cyan-400" /> Cloud infrastructure</span>
        <span className="flex items-center gap-1.5"><HeartPulse className="h-3.5 w-3.5 text-cyan-400" /> Optimized for healthcare</span>
      </div>
    </div>
  </Block>
);

// ---- 3. Multilingual visual: counters + sample bubbles + chip cloud
export const MultilingualBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={Languages} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets}>
    <div>
      <div className="grid grid-cols-3 gap-3 text-center">
        {[
          { v: "15+", l: "International languages" },
          { v: "40+", l: "Regional dialects" },
          { v: "55+", l: "Total, mid-call switching" },
        ].map((s) => (
          <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] px-2 py-4">
            <div className="font-display text-2xl font-bold text-cyan-300">{s.v}</div>
            <div className="mt-1 text-[10px] leading-tight text-slate-500">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 space-y-2.5">
        {languageSamples.slice(0, 4).map((s, i) => (
          <div key={s.lang} className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%] rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-cyan-300/80">{s.lang}</span>
              <p className={`mt-0.5 text-sm text-slate-200 ${s.font === "arabic" ? "font-arabic text-right" : ""}`}>{s.text}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-1.5">
        {internationalLanguages.slice(0, 10).map((l) => (
          <span key={l} className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-1 text-[10px] font-medium text-cyan-200">{l}</span>
        ))}
        {regionalLanguages.slice(0, 6).map((l) => (
          <span key={l} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium text-slate-400">{l}</span>
        ))}
        <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium text-slate-500">+ more</span>
      </div>

      <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
        <UserRound className="h-3.5 w-3.5 text-cyan-400" /> Built for diverse & international patient populations
      </p>
    </div>
  </Block>
);

export const calendarClockIcon = CalendarClock;
